import React, { ReactNode } from 'react';
import { colors, typography, spacing } from '../../styles/design-system';

// Column definition
export interface TableColumn<T> {
  key: string;
  header: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, row: T, index: number) => ReactNode;
}

// Table props
interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  rowKey: keyof T | ((row: T, index: number) => string | number);
  loading?: boolean;
  emptyMessage?: string;
  loadingMessage?: string;
  stickyHeader?: boolean;
  maxHeight?: string;
  onRowClick?: (row: T, index: number) => void;
  rowClassName?: (row: T, index: number) => string;
  headerClassName?: string;
}

function Table<T extends Record<string, any>>({
  columns,
  data,
  rowKey,
  loading = false,
  emptyMessage = '데이터가 없음.',
  loadingMessage = '로딩 중...',
  stickyHeader = false,
  maxHeight,
  onRowClick,
  rowClassName,
  headerClassName,
}: TableProps<T>) {
  const getRowKey = (row: T, index: number): string | number => {
    if (typeof rowKey === 'function') {
      return rowKey(row, index);
    }
    return row[rowKey] as string | number;
  };

  const getCellValue = (row: T, column: TableColumn<T>, index: number): ReactNode => {
    const value = row[column.key];
    if (column.render) {
      return column.render(value, row, index);
    }
    if (value === null || value === undefined) {
      return <span style={{ color: colors.gray[400] }}>-</span>;
    }
    return String(value);
  };

  const containerStyle: React.CSSProperties = {
    ...styles.container,
    ...(maxHeight ? { maxHeight, overflow: 'auto' } : {}),
  };

  const theadStyle: React.CSSProperties = stickyHeader
    ? { position: 'sticky', top: 0, zIndex: 1 }
    : {};

  return (
    <div style={containerStyle}>
      <table style={styles.table}>
        <thead style={theadStyle}>
          <tr className={headerClassName}>
            {columns.map(column => (
              <th
                key={column.key}
                style={{
                  ...styles.th,
                  width: column.width,
                  textAlign: column.align || 'left',
                }}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td
                colSpan={columns.length}
                style={{ ...styles.td, ...styles.centerCell }}
              >
                {loadingMessage}
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                style={{ ...styles.td, ...styles.centerCell }}
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, index) => (
              <tr
                key={getRowKey(row, index)}
                onClick={() => onRowClick?.(row, index)}
                className={rowClassName?.(row, index)}
                style={{
                  ...styles.tr,
                  ...(onRowClick ? styles.clickableRow : {}),
                }}
              >
                {columns.map(column => (
                  <td
                    key={column.key}
                    style={{
                      ...styles.td,
                      textAlign: column.align || 'left',
                    }}
                  >
                    {getCellValue(row, column, index)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    border: `1px solid ${colors.gray[200]}`,
    borderRadius: '4px',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: typography.fontSize.base,
  },
  th: {
    padding: `${spacing[3]} ${spacing[4]}`,
    backgroundColor: colors.gray[100],
    borderBottom: `1px solid ${colors.gray[300]}`,
    fontWeight: typography.fontWeight.semibold,
    color: colors.gray[800],
    whiteSpace: 'nowrap',
  },
  td: {
    padding: `${spacing[3]} ${spacing[4]}`,
    borderBottom: `1px solid ${colors.gray[200]}`,
    color: colors.gray[700],
    whiteSpace: 'nowrap',
  },
  tr: {
    transition: 'background-color 0.15s',
  },
  clickableRow: {
    cursor: 'pointer',
  },
  centerCell: {
    textAlign: 'center',
    padding: spacing[10],
    color: colors.gray[500],
  },
};

export default Table;
